import { useCallback, useMemo, useState } from 'react';

import { HTMLParagraph } from '@/components/HTMLParagraph';
import { CalendarIcon, LocationDotIcon, SearchIcon } from '@/components/Icons';
import { Paragraph } from '@/components/Paragraph';
import { useDeviceBreakpoints } from '@/hooks/useDeviceBreakpoints';

import { ActivityMediaModal } from './ActivityMediaModal';
import { ExcursionBookingForm } from './ExcursionBookingForm';
import { ExcursionBookingFormButton } from './ExcursionBookingFormButton';
import { formatDate } from './helper';
import {
  StyledInfo,
  StyledCardWrapper,
  StyledHeader,
  StyledImageWrapper,
  StyledContent,
  StyledOpenImages,
} from './styles';

import type { ExcursionBookingData } from './ExcursionBookingForm';
import type { ItineraryProduct } from '@/types/products';

interface ExcursionItineraryProduct extends ItineraryProduct {
  type: 'excursion';
}

interface ExcursionCardProps {
  itineraryProduct: ExcursionItineraryProduct;
}

function ExcursionCard({ itineraryProduct }: ExcursionCardProps) {
  const { isMobile } = useDeviceBreakpoints();

  const firstImageInContent = useMemo(
    () => itineraryProduct.Content[0]?.RichContent?.[0].path || '',
    [itineraryProduct.Content],
  );

  const [isImageModalOpened, setIsImageModalOpened] = useState(false);
  const allRichContent = itineraryProduct.Content.flatMap((content) => content.RichContent || []);

  // Booking
  const [isBookingOpened, setIsBookingOpened] = useState(false);
  const bookProduct = useCallback(
    (bookingData: ExcursionBookingData) => {
      console.info('Booking product', {
        productId: itineraryProduct.id,
        ...bookingData,
      });
      setIsBookingOpened(false);
    },
    [itineraryProduct.id],
  );

  return (
    <StyledCardWrapper>
      <StyledContent>
        <StyledImageWrapper $imgSrc={firstImageInContent}>
          <ActivityMediaModal
            showModal={isImageModalOpened}
            closeModal={() => {
              setIsImageModalOpened(false);
            }}
            media={allRichContent}
          />
          <StyledOpenImages
            onClick={() => {
              setIsImageModalOpened(true);
            }}
          >
            <SearchIcon size="lg" />
          </StyledOpenImages>
        </StyledImageWrapper>
        <StyledInfo>
          <StyledHeader>
            <div>
              <h3>{itineraryProduct.name}</h3>
              {itineraryProduct.selected && (
                <Paragraph weight={400} size="lg" flexAlignCenter>
                  <CalendarIcon />
                  &nbsp;
                  {formatDate(itineraryProduct.startDate)}
                </Paragraph>
              )}

              <Paragraph weight={400} size="lg" flexAlignCenter>
                <LocationDotIcon />
                &nbsp;
                {itineraryProduct.Supplier.location}
              </Paragraph>
            </div>
            {!isMobile && (
              <ExcursionBookingFormButton
                onClick={() => setIsBookingOpened(true)}
                isItineraryProductSelected={itineraryProduct.selected}
              />
            )}
          </StyledHeader>

          <HTMLParagraph weight={400} size="lg" pt={8} dangerousHTML={itineraryProduct.Content[0]?.paragraph ?? ''} />
        </StyledInfo>
        {isMobile && (
          <ExcursionBookingFormButton
            onClick={() => setIsBookingOpened(true)}
            isItineraryProductSelected={itineraryProduct.selected}
          />
        )}
      </StyledContent>
      <ExcursionBookingForm isOpened={isBookingOpened} onBooking={bookProduct} />
    </StyledCardWrapper>
  );
}

export { ExcursionCard };
